import { useCallback, useEffect, useRef, useState } from 'react';

import { commitMutation, graphql, useMutation } from 'react-relay';
import type { Disposable, IEnvironment, MutationConfig } from 'relay-runtime';

import type {
	InferredProjectPromotionOutcome,
	InferredProjectPromotionFailure,
} from '../entity-annotations/types';
import type { usePromoteInferredProjectMutation } from './__generated__/usePromoteInferredProjectMutation.graphql';

type PromotedProject = {
	ari: string;
	url: string;
};

export type PromoteInferredProjectState =
	| { status: 'idle' }
	| { status: 'promoting' }
	| { project: PromotedProject; showConfirmation: boolean; status: 'promoted' }
	| { status: 'error' };

const PROJECT_CONFIRMATION_DURATION_MS = 2_000;

export const usePromoteInferredProject = (
	cloudId: string,
	onPromotionResult?: (
		outcome: InferredProjectPromotionOutcome,
		failure?: InferredProjectPromotionFailure,
	) => void,
): {
	promote: (inferredProjectAri: string) => void;
	state: PromoteInferredProjectState;
} => {
	const [state, setState] = useState<PromoteInferredProjectState>({ status: 'idle' });
	const isPromotingRef = useRef(false);
	const confirmationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (confirmationTimerRef.current) {
				clearTimeout(confirmationTimerRef.current);
			}
		},
		[],
	);
	const commitWithQueryContext = useCallback(
		(
			environment: IEnvironment,
			config: MutationConfig<usePromoteInferredProjectMutation>,
		): Disposable =>
			commitMutation<usePromoteInferredProjectMutation>(environment, {
				...config,
				cacheConfig: {
					metadata: {
						headers: {
							'X-Query-Context': `ari:cloud:platform::site/${cloudId}`,
						},
					},
				},
			}),
		[cloudId],
	);
	const [commit] = useMutation<usePromoteInferredProjectMutation>(
		graphql`
			mutation usePromoteInferredProjectMutation(
				$input: GraphInferencePromoteInferredProjectInput!
			) {
				graphInference_promoteInferredProject(input: $input) {
					errors {
						code
						message
					}
					project {
						ari
						url
					}
					success
				}
			}
		`,
		commitWithQueryContext,
	);

	const promote = useCallback(
		(inferredProjectAri: string) => {
			if (isPromotingRef.current || state.status === 'promoted') {
				return;
			}

			isPromotingRef.current = true;
			setState({ status: 'promoting' });
			commit({
				variables: { input: { inferredProjectAri } },
				onCompleted: (response, relayErrors) => {
					isPromotingRef.current = false;
					const payload = response.graphInference_promoteInferredProject;
					if (relayErrors || !payload?.success || !payload.project) {
						setState({ status: 'error' });
						onPromotionResult?.('failed', {
							failureReason: relayErrors
								? 'graphql_error'
								: !payload?.success
									? 'api_failure'
									: 'missing_project',
							errorCodes: payload?.errors?.flatMap(({ code }) => (code ? [code] : [])),
						});
						return;
					}

					onPromotionResult?.('succeeded');
					const promotedProject = payload.project;
					setState({ status: 'promoted', project: promotedProject, showConfirmation: true });
					confirmationTimerRef.current = setTimeout(() => {
						setState((currentState) =>
							currentState.status === 'promoted' && currentState.project.ari === promotedProject.ari
								? { ...currentState, showConfirmation: false }
								: currentState,
						);
					}, PROJECT_CONFIRMATION_DURATION_MS);
				},
				onError: () => {
					isPromotingRef.current = false;
					setState({ status: 'error' });
					onPromotionResult?.('failed', { failureReason: 'transport_error' });
				},
			});
		},
		[commit, state.status, onPromotionResult],
	);

	return { promote, state };
};
